<template>
  <div class="m-auto">
    <div v-if="loading" class="flex justify-center items-center h-64">
      <div class="text-xl text-gray-500">Memuatkan data...</div>
    </div>
    <div v-else>
      <div class="px-4 md:px-8 lg:px-16 py-6 max-w-7xl mx-auto">
        <!-- Profile Picture Upload Card -->
        <div class="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <svg class="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Gambar Profil
          </h2>
          <div class="flex flex-col sm:flex-row sm:items-center gap-4">
            <button
              type="button"
              class="flex items-center justify-center py-2 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-black bg-green-fluorescent hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-opacity"
              @click="$refs.file.click()"
            >
              <span class="align-middle">Tukar Gambar Profil</span>
            </button>
            <button
              v-if="imageData"
              type="button"
              @click="onUpload"
              class="flex items-center justify-center py-2 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-black bg-green-fluorescent hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-opacity"
            >
              <span class="align-middle">Upload</span>
            </button>
            <span
              v-if="imageData"
              class="text-sm font-medium text-gray-700"
            >
              {{ imageData.name }}
            </span>
          </div>
          <input
            ref="file"
            type="file"
            accept="image/*"
            style="display: none"
            @change="previewImage"
          />
        </div>

        <form @submit.prevent="submit()" class="space-y-6">
          <!-- Personal Information Card -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <h2 class="text-xl font-semibold text-gray-800 mb-6 flex items-center border-b pb-3">
              <svg class="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              MAKLUMAT PERIBADI
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Full Name -->
              <div>
                <label for="fullName" class="block text-sm font-medium text-gray-700 mb-1">
                  Nama Penuh
                </label>
                <input
                  v-model="user.full_name"
                  id="fullName"
                  name="fullName"
                  type="text"
                  autocomplete="name"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- NRIC -->
              <div>
                <label for="nric" class="block text-sm font-medium text-gray-700 mb-1">
                  No. Kad Pengenalan
                </label>
                <input
                  v-model="user.ic_no"
                  id="nric"
                  name="nric"
                  type="text"
                  autocomplete="off"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- Address - Full Width -->
              <div class="md:col-span-2">
                <label for="address" class="block text-sm font-medium text-gray-700 mb-1">
                  Alamat
                </label>
                <input
                  v-model="user.address"
                  id="address"
                  name="address"
                  type="text"
                  autocomplete="street-address"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- Postcode -->
              <div>
                <label for="postcode" class="block text-sm font-medium text-gray-700 mb-1">
                  Poskod
                </label>
                <input
                  v-model="user.postcode"
                  id="postcode"
                  name="postcode"
                  type="text"
                  autocomplete="postal-code"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- City -->
              <div>
                <label for="city" class="block text-sm font-medium text-gray-700 mb-1">
                  Bandar
                </label>
                <input
                  v-model="user.city"
                  id="city"
                  name="city"
                  type="text"
                  autocomplete="address-level2"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- State -->
              <div>
                <label for="state" class="block text-sm font-medium text-gray-700 mb-1">
                  Negeri
                </label>
                <input
                  v-model="user.state"
                  id="state"
                  name="state"
                  type="text"
                  autocomplete="address-level1"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- Gender -->
              <div>
                <label for="gender" class="block text-sm font-medium text-gray-700 mb-1">
                  Jantina
                </label>
                <input
                  v-model="user.gender"
                  id="gender"
                  name="gender"
                  type="text"
                  autocomplete="sex"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- Phone Number -->
              <div>
                <label for="phone-no" class="block text-sm font-medium text-gray-700 mb-1">
                  No. Telefon
                </label>
                <input
                  v-model="user.phone_number"
                  id="phone-no"
                  name="phone-no"
                  type="tel"
                  autocomplete="tel"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- Email -->
              <div>
                <label for="user-email" class="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  v-model="user.email"
                  id="user-email"
                  name="email"
                  type="email"
                  autocomplete="email"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
            </div>
          </div>

          <!-- Heir Information Card -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <h2 class="text-xl font-semibold text-gray-800 mb-6 flex items-center border-b pb-3">
              <svg class="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              MAKLUMAT WARIS
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Heir Full Name -->
              <div>
                <label for="heir-fullName" class="block text-sm font-medium text-gray-700 mb-1">
                  Nama Penuh
                </label>
                <input
                  v-model="heirsInformation.full_name"
                  id="heir-fullName"
                  name="heir-fullName"
                  type="text"
                  autocomplete="name"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- Heir NRIC -->
              <div>
                <label for="heir-nric" class="block text-sm font-medium text-gray-700 mb-1">
                  No. Kad Pengenalan
                </label>
                <input
                  v-model="heirsInformation.no_ic"
                  id="heir-nric"
                  name="heir-nric"
                  type="text"
                  autocomplete="off"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- Heir Address - Full Width -->
              <div class="md:col-span-2">
                <label for="heir-address" class="block text-sm font-medium text-gray-700 mb-1">
                  Alamat
                </label>
                <input
                  v-model="heirsInformation.address"
                  id="heir-address"
                  name="heir-address"
                  type="text"
                  autocomplete="street-address"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- Heir Postcode -->
              <div>
                <label for="heir-postcode" class="block text-sm font-medium text-gray-700 mb-1">
                  Poskod
                </label>
                <input
                  v-model="heirsInformation.postcode"
                  id="heir-postcode"
                  name="heir-postcode"
                  type="text"
                  autocomplete="postal-code"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- Heir City -->
              <div>
                <label for="heir-city" class="block text-sm font-medium text-gray-700 mb-1">
                  Bandar
                </label>
                <input
                  v-model="heirsInformation.city"
                  id="heir-city"
                  name="heir-city"
                  type="text"
                  autocomplete="address-level2"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- Heir State -->
              <div>
                <label for="heir-state" class="block text-sm font-medium text-gray-700 mb-1">
                  Negeri
                </label>
                <input
                  v-model="heirsInformation.state"
                  id="heir-state"
                  name="heir-state"
                  type="text"
                  autocomplete="address-level1"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- Heir Gender -->
              <div>
                <label for="heir-gender" class="block text-sm font-medium text-gray-700 mb-1">
                  Jantina
                </label>
                <input
                  v-model="heirsInformation.gender"
                  id="heir-gender"
                  name="heir-gender"
                  type="text"
                  autocomplete="sex"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- Heir Phone Number -->
              <div>
                <label for="heir-phone-no" class="block text-sm font-medium text-gray-700 mb-1">
                  No. Telefon
                </label>
                <input
                  v-model="heirsInformation.phone_number"
                  id="heir-phone-no"
                  name="heir-phone-no"
                  type="tel"
                  autocomplete="tel"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- Heir Email -->
              <div>
                <label for="heir-email" class="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  v-model="heirsInformation.email"
                  id="heir-email"
                  name="heir-email"
                  type="email"
                  autocomplete="email"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
            </div>
          </div>

          <!-- Heir Bank Information Card -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <h2 class="text-xl font-semibold text-gray-800 mb-6 flex items-center border-b pb-3">
              <svg class="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              MAKLUMAT AKAUN BANK WARIS
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Account Holder -->
              <div>
                <label for="acc-holder" class="block text-sm font-medium text-gray-700 mb-1">
                  Nama Pemegang Akaun
                </label>
                <input
                  v-model="heirsInformation.account_holder_name"
                  id="acc-holder"
                  name="acc-holder"
                  type="text"
                  autocomplete="name"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- Account Number -->
              <div>
                <label for="acc-no" class="block text-sm font-medium text-gray-700 mb-1">
                  No. Akaun
                </label>
                <input
                  v-model="heirsInformation.bank_account"
                  id="acc-no"
                  name="acc-no"
                  type="text"
                  autocomplete="off"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
              
              <!-- Bank Name -->
              <div class="md:col-span-2">
                <label for="bank-issuer" class="block text-sm font-medium text-gray-700 mb-1">
                  Nama Bank
                </label>
                <input
                  v-model="heirsInformation.bank_name"
                  id="bank-issuer"
                  name="bank-issuer"
                  type="text"
                  autocomplete="off"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm transition-colors"
                />
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <div class="flex justify-end">
            <button
              type="submit"
              class="w-full sm:w-auto flex items-center justify-center py-3 px-8 border border-transparent rounded-xl shadow-sm text-base font-medium text-black bg-green-fluorescent hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all hover:shadow-lg"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              Simpan Maklumat
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
<script>
import { getAuth, onAuthStateChanged } from "@firebase/auth";
import { doc, getDoc, setDoc, collection, updateDoc } from "firebase/firestore";
import { db } from "@/firebase/db";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "@firebase/storage";
// import { uploadBytes, ref as stRef, storage } from "@firebase/storage";
// import Swal from 'sweetalert2'
// const Swal = require('sweetalert2')
import Swal from "sweetalert2/dist/sweetalert2.js";
export default {
  data() {
    return {
      user: {
        full_name: "",
        ic_no: "",
        address: "",
        postcode: "",
        city: "",
        state: "",
        gender: "",
        phone_number: "",
        email: "",
        bank_account: "",
        image_url: "",
        heir_id: null,
        share_id: null,
        id: null
      },
      heirsInformation: {
        full_name: "",
        ic_no: "",
        address: "",
        postcode: "",
        city: "",
        state: "",
        gender: "",
        phone_number: "",
        email: "",
        account_holder_name: "",
        bank_account: "",
        bank_name: "",
      },
      heir: {},
      share: {},
      imageData: null,
      loading: true,
    };
  },
  async created() {
    this.checkUserState();
  },
  methods: {
    async submit() {
      this.showSlideOver = !this.showSlideOver;
      //?? Check if heir information already exist
      if (this.user && this.user.heir_id) {
        const heirInformation = await getDoc(
          doc(db, "heirs", this.user.heir_id)
        );
        //?? If exist, update the information
        if (heirInformation) {
          const heirsDocRef = doc(db, "heirs", this.user.heir_id);
          this.heirsInformation.updated_at = new Date();

          const heirsDocSnap = await updateDoc(
            heirsDocRef,
            this.heirsInformation
          );

          console.log([heirsDocSnap]);
        }
      } else {
        const heirsDocRef = doc(collection(db, "heirs"));
        const heirsDocSnap = await setDoc(heirsDocRef, this.heirsInformation);
        console.log([heirsDocSnap]);
        this.user.heir_id = heirsDocRef.id;
      }

      const userDocRef = doc(db, "users", this.user.id);

      const userDocSnap = await updateDoc(userDocRef, this.user);
      Swal.fire({
        title: "Tahniah!",
        text: "Data anda telah berjaya dikemaskini.",
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#94de2a",
      });
      console.log([userDocSnap]);
      this.checkUserState();
    },
    checkUserState() {
      const auth = getAuth();
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          console.log("Authenticated");
          const userDocRef = doc(db, "users", user.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            console.log(userData);
            this.user = { ...this.user, ...userData };
            
            if (this.user && this.user.heir_id) {
              const heirInformationDocSnap = await getDoc(
                doc(db, "heirs", this.user.heir_id)
              );
              if (heirInformationDocSnap.exists()) {
                this.heirsInformation = { ...this.heirsInformation, ...heirInformationDocSnap.data() };
                console.log(this.heirsInformation);
              }
            }
            if (this.user && this.user.share_id) {
              const shareInformationDocSnap = await getDoc(
                doc(db, "shares", this.user.share_id)
              );
              if (shareInformationDocSnap.exists()) {
                this.share = shareInformationDocSnap.data();
                console.log(this.share);
              }
            }
            this.loading = false;
          } else {
            console.log("User document does not exist");
            this.loading = false;
          }
        } else {
          console.log("Not authenticated");
          this.$router.push("/");
        }
      });
    },
    onUpload() {
      const storage = getStorage();
      const firebaseCurrentUser = getAuth().currentUser;
      const lastDot = this.imageData.name.lastIndexOf(".");
      const ext = this.imageData.name.substring(lastDot + 1);
      const storageRef = ref(storage, `${firebaseCurrentUser.uid}.${ext}`);
      const uploadTask = uploadBytesResumable(storageRef, this.imageData);
      uploadTask.on(
        `state_changed`,
        (snapshot) => {
          this.uploadValue =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        },
        (error) => {
          console.error(error.message);
        },
        () => {
          this.avatarRefresh = true;
          this.uploadValue = 100;
          getDownloadURL(storageRef).then(async (url) => {
            this.user.imageUrl = url;
            const userDocRef = doc(db, "users", this.user.id);
            await updateDoc(userDocRef, { image_url: this.user.imageUrl });
            Swal.fire({
              title: "Tahniah!",
              text: "Gambar profil anda telah berjaya dikemaskini.",
              icon: "success",
              confirmButtonText: "OK",
              confirmButtonColor: "#94de2a",
            });
          });
        }
      );
    },

    previewImage(event) {
      console.log(event);
      this.uploadValue = 0;
      const imageData = event.target.files[0];
      this.imageData = imageData;
    },
  },
};
</script>

<style scoped>
.bg-green-fluorescent {
  background-color: #94de2a;
}
</style>
